import React, { useLayoutEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import useWindowSize from 'components/hooks/use_window_size';
import { Breakpoints, BreakpointKeys } from 'scripts/presentation/themes/breakpoints';
import { getDevice } from 'scripts/lib/device_detector';
import DeviceContext from './device_context';

export default function DeviceProvider({ children }) {
  const { windowWidth } = useWindowSize();

  // Recompute device on every window-size change so orientation/resize
  // re-detection works (rotating an iPad updates classification). getDevice()
  // reads innerWidth/innerHeight at call time.
  const { device, below } = useMemo(() => ({ device: getDevice(), below: belowKeysFor(windowWidth) }), [windowWidth]);

  // Mirror to document.body so portal-rendered children (modals, toasts) that
  // live outside the React tree still match `[data-device='phone']`. Layout
  // effect runs pre-paint, so the attribute commits in the same frame as the
  // React tree below — no one-frame CSS/JSX mismatch.
  useLayoutEffect(() => {
    document.body.dataset.device = device;
    document.body.dataset.below = below;
    return () => {
      delete document.body.dataset.device;
      delete document.body.dataset.below;
    };
  }, [device, below]);

  // Wrapper div is the in-tree source of truth: attr and tree commit together.
  // `display: contents` so the wrapper is invisible to layout (children appear
  // as if direct siblings of the parent) — required because the existing
  // height: 100% chain in root_content expects no extra layout box.
  return (
    <DeviceContext.Provider value={device}>
      <div className="deviceProvider" data-below={below} data-device={device} style={DEVICE_WRAPPER_STYLE}>
        {children}
      </div>
    </DeviceContext.Provider>
  );
}

const DEVICE_WRAPPER_STYLE = { display: 'contents' };

DeviceProvider.propTypes = {
  children: PropTypes.node,
};

function belowKeysFor(width) {
  return BreakpointKeys.filter(key => width < Breakpoints[key]).join(' ');
}
